/* ============================================================
   nooa-drawer-scroll-lock.js — hold the page still while Dawn's menu drawer is open.

   WHY THIS EXISTS AT ALL. Dawn locks the page by putting `overflow: hidden` on <body>
   (class `overflow-hidden-<breakpoint>`, added by MenuDrawer in global.js). That works
   only through CSS's overflow propagation rule: the viewport takes its overflow from the
   ROOT element, and only if the root is `visible` does the used value come from <body>.
   nooa-base.css sets `html, body { overflow-x: clip }` — deliberately, because `hidden`
   there would make the root a scroll container and kill the sticky header — and `clip` is
   already not `visible`. So the root is the scroll container, body's lock never
   propagates, and Dawn's mechanism is inert in this theme.

   WHY NOT JUST LOCK THE ROOT IN CSS. Because `overflow: hidden` on <html> is what kills
   `position: sticky`. With it, the header stops sticking and sits at the top of the
   DOCUMENT; the drawer is anchored to the header, so opening it half way down the page
   put it off screen above the viewport. Same trap the Tudo build hit, and the same one
   nooa-base.css warns about in its own comment.

   SO THE LOCK IS BEHAVIOURAL, NOT LAYOUT. Nothing here changes a single style: the
   scrolling INPUTS are cancelled while the drawer is open, so no element's overflow, and
   therefore no containing block, changes. Sticky keeps working, the drawer opens where it
   belongs, and the page does not move.

   Scrolling INSIDE the drawer still has to work, so an event whose target sits within a
   scrollable part of the drawer is left alone. `wheel` and `touchmove` must be registered
   as NON-PASSIVE or preventDefault() is ignored.
   ============================================================ */
(function () {
  'use strict';

  if (window.__nooaDrawerScrollLockBound) return;
  window.__nooaDrawerScrollLockBound = true;

  /* Dawn names the class after <header-drawer data-breakpoint>, so both spellings can
     appear depending on the header's menu type, plus the bare one used by other drawers. */
  var LOCK_CLASSES = ['overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop'];

  function isLocked() {
    return LOCK_CLASSES.some(function (c) {
      return document.body.classList.contains(c);
    });
  }

  /* The drawer's own scroll region. Anything inside it keeps its native scrolling. */
  function insideScrollableDrawer(target) {
    if (!(target instanceof Element)) return false;
    return !!target.closest('.menu-drawer__inner-container, .menu-drawer__navigation, .drawer__inner');
  }

  function block(event) {
    if (!isLocked()) return;
    if (insideScrollableDrawer(event.target)) return;
    event.preventDefault();
  }

  window.addEventListener('wheel', block, { passive: false });
  window.addEventListener('touchmove', block, { passive: false });

  /* Keyboard scrolling. Only cancelled when focus is NOT in a field — otherwise space and
     the arrows would stop working for anyone typing in the drawer's own inputs. */
  var SCROLL_KEYS = [' ', 'Spacebar', 'PageUp', 'PageDown', 'End', 'Home', 'ArrowUp', 'ArrowDown'];

  window.addEventListener(
    'keydown',
    function (event) {
      if (!isLocked()) return;
      if (SCROLL_KEYS.indexOf(event.key) === -1) return;

      var el = event.target;
      if (el && el.closest && el.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (insideScrollableDrawer(el)) return;

      event.preventDefault();
    },
    { passive: false }
  );
})();

/* ============================================================
   The drawer's own close button.

   The design's drawer is `position: fixed; inset: 0`, so it covers the header bar and the
   burger that opened it — hence a close control inside the panel. It does NOT re-implement
   closing: it forwards a click to Dawn's own <summary>, so `MenuDrawer.onSummaryClick`
   runs untouched and everything it owns still happens — focus returns to the summary,
   `menu-opening` is cleared, the body class comes off and the scroll lock above releases.

   Delegated from the document so it survives the theme editor replacing the section.
   ============================================================ */
(function () {
  'use strict';

  if (window.__nooaDrawerCloseBound) return;
  window.__nooaDrawerCloseBound = true;

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-nooa-drawer-close]');
    if (!button) return;

    var details = button.closest('details');
    var summary = details && details.querySelector('summary');
    if (!summary) return;

    event.preventDefault();
    summary.click();
  });
})();
